import React, { useEffect, useCallback, useRef, useState } from "react";
import { Box, Container, CircularProgress, Alert } from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../utils/api";

/* ================= UI COMPONENTS ================= */
import ExamHeader from "./sessionFile/ExamHeader";
import ExamQuestionBody from "./sessionFile/ExamQuestionBody";
import JambExamModal from "./sessionFile/JambExamModal";

/* ================= CORE ENGINE ================= */
import useSingleExamSession from "./hooks/useSingleExamSession";

/**
 * =========================================================
 * JAMB EXAM SESSION PAGE (4-STRAIGHT, BACKEND-DRIVEN)
 * =========================================================
 */

export default function JambExamSessionPage() {
  const { examId } = useParams();
  const navigate = useNavigate();

  /* ================= AUTOSUBMIT BRIDGE ================= */
  const autoSubmitRef = useRef(null);
  const autoSubmitArmedRef = useRef(false);

  /* ================= CORE EXAM ENGINE ================= */
  const single = useSingleExamSession({
    examId,
    api,
    onAutoSubmit: () => autoSubmitRef.current?.(),
  });

  /* ================= LOCAL UI STATE ================= */
  const [loading, setLoading] = useState(true);
  const [confirmSubmit, setConfirmSubmit] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  const [chainError, setChainError] = useState("");

  const [finalSuccessOpen, setFinalSuccessOpen] = useState(false);
  const [finalAttemptId, setFinalAttemptId] = useState(null);

  /* ================= EXTRACT FROM ENGINE ================= */
  const {
    exam,
    questions,
    answers,
    flags,
    currentIndex,
    currentQ,
    remainingSec,
    saving,
    navBusy,
    submittingUI,

    setExam,
    setQuestions,
    setAnswers,
    setFlags,
    setCurrentIndex,
    setRemainingSec,
    setSubmittingUI,

    flushQuestionIfDirty,
    handleSelectOption,
    toggleFlag,
    setIndexSafely,
    stopAllIntervals,

    submittingRef,
    currentQIdRef,
  } = single;

  /* ================= LOAD SESSION ================= */
  const loadSession = useCallback(async () => {
    try {
      setLoading(true);

      const res = await api.get(`/exam/session/${examId}`);
      const examObj = res.data?.exam;

      setExam(examObj || null);
      setQuestions(res.data?.questions || []);

      const ans = {};
      const flg = {};
      const rawAnswers = res.data?.answers;

      if (Array.isArray(rawAnswers)) {
        rawAnswers.forEach((a) => {
          if (!a) return;
          ans[a.question_id] = a.selected_option;
          flg[a.question_id] = !!a.flagged;
        });
      } else if (rawAnswers && typeof rawAnswers === "object") {
        Object.entries(rawAnswers).forEach(([qid, val]) => {
          ans[qid] = val;
        });
      }

      setAnswers(ans);
      setFlags(flg);

      const rem = Number.isInteger(examObj?.remaining_seconds)
        ? Math.max(0, examObj.remaining_seconds)
        : Math.max(0, Math.floor((examObj?.remaining_minutes ?? 15) * 60));

      setRemainingSec(rem);
      setCurrentIndex(0);

      autoSubmitArmedRef.current = rem > 0;
    } catch (err) {
      setChainError(
        err?.response?.data?.message ||
          "Unable to load JAMB subject. Please refresh."
      );
    } finally {
      setLoading(false);
    }
  }, [
    examId,
    setExam,
    setQuestions,
    setAnswers,
    setFlags,
    setRemainingSec,
    setCurrentIndex,
  ]);

  /* ================= SUBMIT (JAMB ONLY) ================= */
  const handleSubmit = useCallback(
    async (auto = false) => {
      if (submittingRef.current && !auto) return;

      submittingRef.current = true;
      setSubmittingUI(true);
      setChainError("");

      try {
        const qid = currentQIdRef.current;
        if (qid) await flushQuestionIfDirty(qid);

const submitRes = await api.post(`/exam/${examId}/submit`, {
  remaining_seconds: remainingSec,
  auto: !!auto,
});

const submitAttemptId = submitRes.data?.jamb_attempt_id || null;





        stopAllIntervals();

        const nextRes = await api.post("/jamb/next-subject");
        const next = nextRes.data;

     if (next?.completed) {
  const finalId = next.jamb_attempt_id || submitAttemptId;

  if (!finalId) {
    throw new Error("JAMB completed but missing attempt id.");
  }

  setFinalAttemptId(finalId);
  setConfirmSubmit(false);

  setTimeout(() => setFinalSuccessOpen(true), 200);
  return;
}


        if (next?.subject_id) {
          const startRes = await api.post("/exam/start", {
            subject_id: next.subject_id,
            exam_type: "JAMB",
          });

          const nextExamId = startRes.data?.exam?.id;
          if (!nextExamId) {
            throw new Error("Failed to start next JAMB subject.");
          }

          navigate(`/exam/jamb/session/${nextExamId}`, { replace: true });
          return;
        }

        throw new Error("Unexpected JAMB next-subject response.");
      } catch (err) {
        setChainError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to submit subject. Please retry."
        );
      } finally {
        submittingRef.current = false;
        setSubmittingUI(false);
      }
    },
    [
      examId,
      remainingSec,
      navigate,
      flushQuestionIfDirty,
      stopAllIntervals,
      setSubmittingUI,
      submittingRef,
      currentQIdRef,
    ]
  );

  /* ================= AUTOSUBMIT BRIDGE ================= */
  useEffect(() => {
    autoSubmitRef.current = () => handleSubmit(true);
  }, [handleSubmit]);

  useEffect(() => {
    if (
      autoSubmitArmedRef.current &&
      remainingSec === 0 &&
      !submittingRef.current
    ) {
      autoSubmitArmedRef.current = false;
      autoSubmitRef.current?.();
    }
  }, [remainingSec, submittingRef]);

  /* ================= INIT ================= */
  useEffect(() => {
    loadSession();
    return () => stopAllIntervals();
  }, [loadSession, stopAllIntervals]);

  /* ================= RESET UI ON SUBJECT CHANGE ================= */
  useEffect(() => {
    stopAllIntervals();
    setConfirmSubmit(false);
    setChainError("");
    submittingRef.current = false;
    currentQIdRef.current = null;
    setMapOpen(false);
    autoSubmitArmedRef.current = false;
  }, [examId, stopAllIntervals, submittingRef, currentQIdRef]);

  /* ================= UI HELPERS ================= */
  const minutes = Math.floor(remainingSec / 60);
  const seconds = remainingSec % 60;

  const canSubmit = Object.keys(answers || {}).length > 0;

  const TOTAL_JAMB_SUBJECTS = 4;

  const currentSubjectIndex = Number.isInteger(exam?.jamb_subject_index)
    ? exam.jamb_subject_index
    : Number.isInteger(exam?.subject_order)
    ? exam.subject_order
    : null;

  const subjectProgress =
    Number.isInteger(currentSubjectIndex) && TOTAL_JAMB_SUBJECTS > 0
      ? Math.min(
          100,
          Math.round((currentSubjectIndex / TOTAL_JAMB_SUBJECTS) * 100)
        )
      : 0;

  const isFinalSubject =
    Number.isInteger(currentSubjectIndex) &&
    currentSubjectIndex === TOTAL_JAMB_SUBJECTS;

  /* ================= RENDER ================= */
  if (loading) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (!exam) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Unable to load JAMB subject.</Alert>
      </Container>
    );
  }

  return (
    <Container
      maxWidth="lg"
      className="exam-lock"
      sx={{
        px: { xs: 1, sm: 2, md: 3 },
        py: { xs: 1, sm: 2 },
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh - 80px)",
        }}
      >
        <ExamHeader
          exam={exam}
          minutes={minutes}
          seconds={seconds}
          setMapOpen={setMapOpen}
          subjectIndex={currentSubjectIndex}
          totalSubjects={TOTAL_JAMB_SUBJECTS}
          subjectProgress={subjectProgress}
          saving={saving}
          navBusy={navBusy}
          submittingUI={submittingUI}
        />

        <Box
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            mt: { xs: 1, sm: 2 },
          }}
        >
          <ExamQuestionBody
            questions={questions}
            currentIndex={currentIndex}
            answers={answers}
            flags={flags}
            currentQ={currentQ}
            setIndexSafely={setIndexSafely}
            handleSelectOption={handleSelectOption}
            toggleFlag={toggleFlag}
            saving={saving}
            navBusy={navBusy}
            submittingUI={submittingUI}
            canSubmit={canSubmit}
            setConfirmSubmit={() => {
              if (!canSubmit) return;
              setConfirmSubmit(true);
            }}
          />
        </Box>

        <JambExamModal
          mapOpen={mapOpen}
          setMapOpen={setMapOpen}
          questions={questions}
          setIndexSafely={setIndexSafely}
          confirmSubmit={confirmSubmit}
          setConfirmSubmit={setConfirmSubmit}
          answers={answers}
          handleSubmit={handleSubmit}
          submittingUI={submittingUI}
          chainError={chainError}
          finalSuccessOpen={finalSuccessOpen}
          onViewAnalysis={() =>
            navigate(`/dashboard/jamb-analysis/${finalAttemptId}`, {
              replace: true,
            })
          }
          isFinalSubject={isFinalSubject}
        />
      </Box>
    </Container>
  );
}
